// import Vue from 'vue'
import { uuid } from 'vue-uuid';

import EventUtils from '@/utils/EventUtils.js';
import StringUtils from '@/utils/StringUtils.js';
import WebLogger from "@/domain/logger/WebLogger.js";

import UserEvents from '@/domain/model/user/UserEvents.js';

const API_HOST = process.env.VUE_APP_RC_API;
const SOCKET_URL = API_HOST.replace(/^http/, 'ws');
const log = new WebLogger(window.console, true);

const state = {
  socket: {
    message: null,
    connection: null,
    isConnected: false,
    reconnectError: false,
    socket: null,
    count: 0,
    error: null,
  },
  // VUE_APP_RC_CDN defined in .env.development 
  client: {
    user: null,
    deviceId: uuid.v1(),
    version: process.env.VUE_APP_RC_VERSION,
    date: process.env.VUE_APP_RC_DATE,
    apiHost: process.env.VUE_APP_RC_API,
    cdnHost: process.env.VUE_APP_RC_CDN,
    debug: process.env.VUE_APP_RC_DEBUG == 'true',
    socket: null,
    connected: function () {
      if (this.socket) {
        return this.socket.isConnected;
      }
      return false;
    },
    isProduction() {
      if (StringUtils.contains(process.env.VUE_APP_RC_API, "localhost")) {
        return false;
      }
      return true;
    },
    cookie: null,
  },
  domain: null,
  userRoles: null,
  company: null,
  groupLabelId: null,
  divisionLabelId: null,
  originalCompany: null,
};

const getters = {
  auth_client: state => state.client,
  auth_socket: state => state.socket,
  auth_connected: state => state.socket.isConnected,
  auth_socket_message: state => state.socket.message,
  auth_socket_status: state => state.socket.message ? state.socket.message.progressMessage : "",
  auth_user: state => state.client.user,
  user_roles: state => state.userRoles,

  domain: state => state.domain,

  AuthStore_socketError: state => state.socket.error,
  AuthStorage_Company: state => state.company,
  AuthStorage_GroupLabelId: state => state.groupLabelId,
  AuthStorage_DivisionLabelId: state => state.divisionLabelId,

  AuthStorage_Session: function () {
    var value = window.localStorage.readyChek;
    var cookieData = JSON.parse(value);
    if (cookieData) {
      //
    }
    return cookieData
  },
  authStorage_isConnected: function () {
    if (state.socket.isConnected) {
      return true;
    }
    var value = window.localStorage.readyChek;
    var cookieData = JSON.parse(value);
    if (cookieData) {
      //
      var time = cookieData['time'];
      if (time) {
        //
      }
    }
    return false;
  },
  getCookie: () => () => {
    const value = window.localStorage.readyChek;
    if (value) {
      return JSON.parse(value);
    }
    return null;
  },
  AuthStorage_OriginalCompany: state => state.originalCompany,
};

const actions = {
  updateUser({ commit }, user) {
    commit('setUser', user);
  },
  updateDomain({ commit }, domain) {
    commit('setDomain', domain);
  },
  authClear({ commit }) {
    commit('authClear');
  },
  AuthStorage_updateCompany({ commit }, company) {
    commit('setCompany', company);
  },
  AuthStorage_updateGroupLabel({ commit }, groupLabelId) {
    commit('setGroupLabel', groupLabelId);
  },
  AuthStorage_updateDivisionLabel({ commit }, divisionLabelId) {
    commit('setDivisionLabel', divisionLabelId);
  },
  connectSocket({ commit, dispatch, state }) {
    if (state.socket.isConnected || (state.client.socket && state.client.socket.readyState === WebSocket.OPEN)) {
      console.warn("Socket connection already exists.");
      return;
    }

    const socket = new WebSocket(SOCKET_URL);

    socket.onopen = (event) => {
      commit('SOCKET_ONOPEN', event);
    };

    socket.onmessage = (message) => {
      const event = JSON.parse(message.data);
      if (event.eventName == UserEvents.SignIn.Names.Response) {
        if (event.status == EventUtils.STATUS.COMPLETE && event.statusCode == EventUtils.CODE.OK) {
          commit('setUser', event.authUser);
          commit('SET_SOCKET', socket);
        }
      }
      if (event.name === 'statusUpdate' || event.progressMessage) {
        commit('SET_SOCKET_STATUS', event.progressMessage);
      }
      commit('SOCKET_ONMESSAGE', event);
    };

    socket.onclose = (event) => {
      commit('SOCKET_ONCLOSE', event);
      const value = window.localStorage.readyChek;
      if (value) {
        log.info("WebSocket connection closed. Reconnecting in 5 seconds...");
        setTimeout(() => {
          dispatch('connectSocket');
        }, 5000);
      }
    };

    socket.onerror = (error) => {
      commit('SOCKET_ONERROR', error);
    };
  },
};

const mutations = {
  authClear(state) {
    state.client.user = null;
    state.socket.message = null;
    state.socket.isConnected = false;
    state.socket.connection = null;
    state.socket.message = null;
  },

  setUser(state, user) {
    state.client.user = user;
  },

  setDomain(state, domain) {
    state.domain = domain;
    state.userRoles = domain.session().employee().roles().find();
  },

  setCompany(state, company) {
    if (company && company.isNotNew()) {
      if (!company.isDivision()) {
        state.originalCompany = company;
      }
    }
    state.groupLabelId = null;
    state.company = company;
  },

  setGroupLabel(state, groupLabelId) {
    state.groupLabelId = groupLabelId;
  },

  setDivisionLabel(state, divisionLabelId) {
    state.divisionLabelId = divisionLabelId;
  },

  SOCKET_ONOPEN(state, event) {
    state.socket.connection = event.currentTarget;
    state.client.socket = state.socket;
    state.socket.isConnected = true;
    state.socket.message = event;
    if (state.domain != null && state.domain.client() != null) {
      state.domain.client().sendQueuedMessages();
    }
  },

  SOCKET_ONCLOSE(state, event) {
    state.socket.isConnected = false;
    state.socket.message = event;
  },

  SOCKET_ONERROR(state, event) {
    state.socket.message = event;
    state.socket.error = event;
  },

  SOCKET_ONMESSAGE(state, event) {
    if (event.status == EventUtils.STATUS.COMPLETE &&
      event.eventName == UserEvents.SignIn.Names.Response &&
      event.statusCode == EventUtils.CODE.OK) {
      state.client.user = event.authUser;
      state.client.socket = state.socket;
    }
    if (state.domain != null && state.domain.client() != null) {
      //state.domain.logger().info("Response:\n{0}", [JSON.stringify(event, null, 0)])
    }
    setTimeout(() => {
      state.socket.message = event
    }, 1500);
  },

  SOCKET_RECONNECT(state, count) {
    state.socket.count = count;
  },

  SOCKET_RECONNECT_ERROR(state) {
    state.socket.reconnectError = true;
  },

  SET_AUTH_CONNECTED(state, isConnected) {
    state.socket.isConnected = isConnected;
  },
  SET_SOCKET(state, socket) {
    state.client.socket = socket;
    state.socket.socket = socket;
    state.socket.connection = socket;
  },
  SET_SOCKET_STATUS(state, status) {
    if (!state.socket.message) state.socket.message = {};
    state.socket.message.progressMessage = status;
  },
  SET_SOCKET_ERROR(state, error) {
    state.socket.error = error;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};